/** Shared marketplace enums / labels for UI. */

/** Strip formatting; store digits only (e.g. 60123456789). */
export function normalizeWhatsAppPhone(raw: string): string {
  return raw.replace(/\D/g, "");
}

/** Build wa.me link from stored phone (digits only, min 8). */
export function buildWhatsAppUrl(phone: string | null | undefined): string | null {
  const digits = normalizeWhatsAppPhone(phone ?? "");
  if (digits.length < 8) return null;
  return `https://wa.me/${digits}`;
}

export const MARKETPLACE_LISTING_CONDITIONS = ["NM", "LP", "MP", "HP", "DMG"] as const;

export type MarketplaceListingCondition = (typeof MARKETPLACE_LISTING_CONDITIONS)[number];

export const SELLER_PROFILE_STATUS_LABELS: Record<string, string> = {
  DRAFT: "Draft",
  KYC_SUBMITTED: "Information pending",
  KYC_REJECTED: "Information rejected",
  VERIFICATION_REQUIRED: "Update required",
  ACTIVE: "Active",
  SUSPENDED: "Suspended",
};

export const MARKETPLACE_LISTING_STATUS_LABELS: Record<string, string> = {
  DRAFT: "Draft",
  PENDING_REVIEW: "Pending review",
  ACTIVE: "Active",
  SOLD_OUT: "Sold out",
  DELISTED: "Delisted",
};

/** Default label for the marketplace in-person handover option (setting `marketplace.collect_label`). */
export const DEFAULT_MARKETPLACE_COLLECT_LABEL = "COD";

export const MARKETPLACE_SELLER_FULFILMENT_STATUSES = [
  "TO_SHIP",
  "PROCESSING",
  "SHIPPED",
  "READY_FOR_COLLECTION",
  "COMPLETED",
  "BUYER_CONFIRMED",
  "CANCELLED",
] as const;

export type MarketplaceSellerFulfilmentStatus = (typeof MARKETPLACE_SELLER_FULFILMENT_STATUSES)[number];

export function marketplaceFulfilmentStatusLabel(
  status: string | null | undefined,
  collectLabel: string = DEFAULT_MARKETPLACE_COLLECT_LABEL,
): string {
  if (!status) return "—";
  switch (status as MarketplaceSellerFulfilmentStatus) {
    case "TO_SHIP":
      return "Pending";
    case "PROCESSING":
      return "Processing";
    case "SHIPPED":
      return "Shipped";
    case "READY_FOR_COLLECTION":
      return `Ready for ${collectLabel}`;
    case "COMPLETED":
      return "Completed";
    case "BUYER_CONFIRMED":
      return "Buyer confirmed";
    case "CANCELLED":
      return "Cancelled";
    default:
      return status.replace(/_/g, " ");
  }
}

export const MARKETPLACE_FULFILMENT_STATUS_LABELS: Record<string, string> = Object.fromEntries(
  MARKETPLACE_SELLER_FULFILMENT_STATUSES.map((s) => [s, marketplaceFulfilmentStatusLabel(s)]),
);

/** Customer/seller-facing name of an order's fulfilment method (`PICKUP` on marketplace = COD). */
export function marketplaceFulfilmentMethodLabel(
  fulfilmentMethod: string | null | undefined,
  collectLabel: string = DEFAULT_MARKETPLACE_COLLECT_LABEL,
): string {
  return fulfilmentMethod === "PICKUP" ? collectLabel : "Delivery";
}

/** Customer-facing marketplace order progress (0-based step index, or -1 if unknown / cancelled). */
export function marketplaceBuyerProgressStep(
  fulfilmentMethod: string,
  sellerFulfilmentStatus: string | null | undefined,
  buyerConfirmed: boolean,
): number {
  if (buyerConfirmed || sellerFulfilmentStatus === "BUYER_CONFIRMED") return 4;
  const pickup = fulfilmentMethod === "PICKUP";
  switch (sellerFulfilmentStatus) {
    case "TO_SHIP":
      return 0;
    case "PROCESSING":
      return 1;
    case "SHIPPED":
      return pickup ? 1 : 2;
    case "READY_FOR_COLLECTION":
      return pickup ? 2 : 1;
    case "COMPLETED":
      return 3;
    case "CANCELLED":
      return -1;
    default:
      return sellerFulfilmentStatus ? 0 : -1;
  }
}

export function marketplaceBuyerProgressLabels(
  fulfilmentMethod: string | null | undefined,
  collectLabel: string = DEFAULT_MARKETPLACE_COLLECT_LABEL,
): string[] {
  if (fulfilmentMethod === "PICKUP") {
    return ["Pending", "Processing", `Ready for ${collectLabel}`, `${collectLabel} completed`, "Confirmed"];
  }
  return ["Pending", "Processing", "Shipped", "Delivered", "Confirmed"];
}

export const MARKETPLACE_BUYER_PROGRESS_LABELS = {
  delivery: marketplaceBuyerProgressLabels("DELIVERY"),
  pickup: marketplaceBuyerProgressLabels("PICKUP"),
} as const;

export const MARKETPLACE_PAYOUT_STATUS_LABELS: Record<string, string> = {
  HELD: "Held",
  READY: "Ready to settle",
  SETTLED: "Settled",
};

export const SELLER_SETTLEMENT_BATCH_STATUS_LABELS: Record<string, string> = {
  DRAFT: "Draft",
  FINALIZED: "Awaiting payout",
  PAID_OUT: "Paid out",
};

/** Seller portal shipping provider presets (per order; not tied to Easy Parcel). */
export const MARKETPLACE_COURIER_PRESETS = [
  "GDEX",
  "Pos Laju",
  "J&T",
  "Ninja Van",
  "DHL",
  "City-Link",
  "Other",
] as const;

export type MarketplaceCourierPreset = (typeof MARKETPLACE_COURIER_PRESETS)[number];

export const MARKETPLACE_COURIER_PRESET_OTHER: MarketplaceCourierPreset = "Other";

/** Map stored order.courier to preset dropdown + optional custom label. */
export function resolveMarketplaceCourierPreset(stored: string | null | undefined): {
  preset: MarketplaceCourierPreset | "";
  custom: string;
} {
  const trimmed = stored?.trim() ?? "";
  if (!trimmed) return { preset: "", custom: "" };
  if (trimmed === MARKETPLACE_COURIER_PRESET_OTHER) return { preset: MARKETPLACE_COURIER_PRESET_OTHER, custom: "" };
  if ((MARKETPLACE_COURIER_PRESETS as readonly string[]).includes(trimmed)) {
    return { preset: trimmed as MarketplaceCourierPreset, custom: "" };
  }
  return { preset: MARKETPLACE_COURIER_PRESET_OTHER, custom: trimmed };
}

/** Single carrier string for API from UI preset + custom Other field. */
export function marketplaceCourierToApi(preset: string, custom: string): string {
  if (preset === MARKETPLACE_COURIER_PRESET_OTHER) return custom.trim();
  return preset.trim();
}
